import React, { useEffect, useMemo, useRef, useState } from "react";
import { Target, Phone, Presentation, Clock, Users, Search, Ban } from "lucide-react";
import { apiGet, unpackList } from "../../../Api/Client.js";
import RadarChart from "./RadarChart.jsx";
import CallCard from "./CallCard.jsx";
import KeyMoments from "./KeyMoments.jsx";
import GapBars from "./GapBars.jsx";
import VoiceTimeline from "./VoiceTimeline.jsx";
import TranscriptAnalyser from "./TranscriptAnalyser.jsx";
import {
  callAverage, companyOf, durationOf, formatDate, formatDuration, formatStartTime,
  isRefused, MISS_AT, participantsOf, plural, refusalLabel, scoreColor,
} from "./callMeta.js";

// The Seller Scorecard, built as a manager's drill-down:
//
//   pick a person → see their radar → scan their calls → open one → read it
//
// It used to be a grid of small cards, one per employee, which answers no
// question anybody actually has. A manager opening this has one of two
// questions — "how is this person doing" or "what happened on that call" — and
// they are one click apart, so the page is one person with their calls beside
// them rather than everybody at once.
//
// A rep sees the same layout with no picker, because there is nothing to pick:
// the API scopes rows to the caller, so this is not the frontend deciding who
// may read what.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const NO_MEANINGS = {};
const muted = { color: "var(--c-500)" };

const MODALITIES = [
  { key: "call", label: "Phone calls", noun: "phone call", hint: "Cold calls, callbacks and follow-ups", icon: Phone },
  { key: "meeting", label: "Demos & meetings", noun: "demo", hint: "AE / demo-room craft", icon: Presentation },
];

function shortDay(call) {
  const d = new Date(`${String(call?.call_date || "").slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// "Your last 5 calls, averaged" (Docs/SCORECARD_IN_PLAIN_ENGLISH.md), and it
// has to mean five calls that were actually scored. A refused call has no
// scores and a CRM-outcomes row is not a call, and both used to take one of
// the five slots -- so the radar was quietly built from three calls while
// the card said "28 scored".
const RECENT_CALLS = 5;

export function scoredCallsOf(calls) {
  return calls.filter((c) => !isRefused(c) && c.source !== "crm");
}

function scoreOn(call, key) {
  const p = (call.parameters || []).find((x) => x.key === key);
  // Unobserved is excluded, never averaged in as a zero: the scorer
  // abstains when a call gave no evidence, and folding those in would
  // punish a rep for a conversation that never went there.
  return p && p.observed !== false && typeof p.score === "number" ? p.score : null;
}

export function averagesByParameter(calls, parameters) {
  const recent = scoredCallsOf(calls).slice(0, RECENT_CALLS);
  return parameters.map((p) => {
    // A skill measured from the CRM has no call to come from: its number is
    // the last five times it WAS measured, CRM checks included.
    const pool = p.dimension === "outcome"
      ? calls.filter((c) => !isRefused(c) && scoreOn(c, p.key) != null).slice(0, RECENT_CALLS)
      : recent;
    const values = pool.map((c) => scoreOn(c, p.key)).filter((v) => v != null);
    return {
      ...p,
      score: values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10 : null,
      calls_scored: values.length,
    };
  });
}

function dimensionAverages(calls) {
  const buckets = { tonality: [], technicality: [] };
  scoredCallsOf(calls).slice(0, RECENT_CALLS).forEach((c) => {
    Object.entries(c.dimension_scores || {}).forEach(([k, v]) => {
      if (typeof v === "number" && buckets[k]) buckets[k].push(v);
    });
  });
  const out = {};
  Object.entries(buckets).forEach(([k, v]) => {
    if (v.length) out[k] = Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10;
  });
  return out;
}

function ScoreBar({ label, score, ten, zero, means, calls }) {
  const pct = score == null ? 0 : Math.max(0, Math.min(10, score)) * 10;
  const color = score == null ? "var(--c-300)" : scoreColor(score);
  // What the skill IS, always; and on a skill at or below the miss line, what
  // pulls that score down and what a 10 does instead. The `zero` anchor used
  // to be withheld as "an insult attached to a person"; reps asked to be told
  // what they are doing wrong (2026-09-24), so it is shown -- as a
  // description of the behaviour, only where the score is actually low, and
  // always beside the target.
  const low = score != null && score <= MISS_AT;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 78px", alignItems: "start",
                  gap: "2px 10px", padding: "6px 0" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "var(--c-800)", marginBottom: 2, fontWeight: 600 }}>{label}</div>
        {means && <div style={{ fontSize: 11, ...muted, marginBottom: 4, lineHeight: 1.35 }}>{means}</div>}
        <div style={{ height: 5, borderRadius: 999, background: "var(--c-100, rgba(120,120,120,.15))", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 999 }} />
        </div>
      </div>
      <div style={{ textAlign: "right", paddingTop: 1 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
          {score == null ? "—" : Number(score).toFixed(1)}
        </span>
        {/* The denominator travels with the number everywhere it appears --
            spelled out. "3c" needed a paragraph of the explainer doc to read. */}
        <div style={{ fontSize: 10, ...muted, lineHeight: 1.2 }}>
          {calls ? `${calls} call${calls === 1 ? "" : "s"}` : "not seen yet"}
        </div>
      </div>
      {(low && (zero || ten)) && (
        <div style={{ gridColumn: "1 / -1", fontSize: 11, lineHeight: 1.4, marginTop: 4, padding: "6px 8px",
                      borderRadius: 7, background: "var(--c-100, rgba(120,120,120,.1))", color: "var(--c-700)" }}>
          {zero && <div><strong style={{ color: "var(--red-600, #c0392b)" }}>What pulls it down:</strong> {zero}</div>}
          {ten && <div style={{ marginTop: zero ? 2 : 0 }}><strong style={{ color: "var(--green-600)" }}>Aim for:</strong> {ten}</div>}
        </div>
      )}
    </div>
  );
}

function RadarBlock({ modality, calls, parameters, median = null, meanings = {} }) {
  const averaged = useMemo(
    () => averagesByParameter(calls, parameters).map((p) => ({ ...p, means: meanings[p.key] || "" })),
    [calls, parameters, meanings],
  );
  const scored = scoredCallsOf(calls);
  const refused = calls.filter(isRefused).length;
  const recent = scored.slice(0, RECENT_CALLS);
  if (!parameters.length) return null;
  const Icon = modality.icon;
  // Only drawn when the server says the comparison is meaningful. A "team
  // median" computed over one other rep is that rep, and a dashed polygon
  // labelled "team" is a claim the data cannot support — so the endpoint
  // returns `comparable: false` and no axes, and nothing is drawn.
  const overlay = median?.comparable ? median.axes : null;

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Icon size={14} color="var(--blue-600)" />
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--c-900)" }}>{modality.label}</div>
            <div style={{ fontSize: 10.5, ...muted }}>{modality.hint}</div>
          </div>
        </div>
        <span style={{ fontSize: 10.5, ...muted, textAlign: "right" }}>
          {scored.length ? `${scored.length} scored` : "none scored yet"}
          {refused ? ` · ${refused} not scored` : ""}
        </span>
      </div>

      {scored.length === 0 ? (
        <p style={{ fontSize: 12, ...muted, margin: "10px 0 0" }}>
          No {modality.label.toLowerCase()} scored yet.
        </p>
      ) : (
        <>
          {/* Radar = judgements only. Measures have healthy bands rather than
              directions, and live on the call card as gauges. */}
          <div style={{ marginTop: 8 }}>
            <RadarChart axes={averaged} size={230} overlay={overlay}
                        overlayLabel={`Team median · ${median?.reps ?? 0} reps`} />
          </div>
          {/* What the numbers are built from, said once, where they are. */}
          <p style={{ fontSize: 11, ...muted, margin: "8px 0 0", lineHeight: 1.45 }}>
            Each number is the average of the last {plural(recent.length, `scored ${modality.noun}`)}
            {recent.length ? ` (${shortDay(recent[recent.length - 1])} to ${shortDay(recent[0])})` : ""}, out of 10.
            The small grey number is how many of them the skill came up in. Further out on the shape is better.
          </p>
          <div style={{ marginTop: 8, paddingTop: 4, borderTop: "1px solid var(--c-200)" }}>
            {averaged.map((p) => (
              <ScoreBar key={p.key} label={p.label} score={p.score} ten={p.ten} zero={p.zero}
                        means={p.means} calls={p.calls_scored} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MetaBit({ icon: Icon, children, title }) {
  if (children == null || children === "") return null;
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
      <Icon size={10} /> {children}
    </span>
  );
}

function CallRow({ call, active, onClick }) {
  const { avg, count } = callAverage(call);
  const refused = isRefused(call);
  const color = scoreColor(avg);
  const company = companyOf(call);
  const start = formatStartTime(call.started_at);
  const duration = formatDuration(durationOf(call));
  const people = participantsOf(call);

  // The selected row has to be unmistakable. It was a 1px border in a CSS
  // variable plus a very faint tint, which on a list of near-identical rows
  // read as "nothing happened" — the card underneath was updating correctly
  // and the click still felt broken. A left accent bar, a real tint and a
  // bolder label make the selection legible at a glance.
  return (
    <button onClick={onClick} aria-current={active ? "true" : undefined} style={{
      display: "grid", gridTemplateColumns: "1fr auto", gap: 8, width: "100%", textAlign: "left",
      padding: "9px 10px 9px 12px", borderRadius: 9, cursor: "pointer",
      border: "1px solid transparent",
      borderLeft: `3px solid ${active ? "var(--blue-600)" : "transparent"}`,
      background: active ? "rgba(80,120,255,.13)" : "transparent",
    }}>
      <div style={{ minWidth: 0 }}>
        {/* Company first when there is one: it is how a manager remembers a
            call ("the Acme demo"). Otherwise the title, then the date — several
            calls legitimately share a label, and the date line underneath is
            what tells them apart. */}
        <div style={{ fontSize: 12.5, color: active ? "var(--c-900)" : "var(--c-800)",
                      fontWeight: active ? 700 : 600,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {company || call.call_label || formatDate(call)}
        </div>
        <div style={{ fontSize: 10.5, ...muted, marginTop: 2, display: "flex", flexWrap: "wrap",
                      columnGap: 8, rowGap: 2 }}>
          <span style={{ whiteSpace: "nowrap" }}>{formatDate(call)}{start ? ` · ${start}` : ""}</span>
          <MetaBit icon={Clock} title="Call length">{duration}</MetaBit>
          <MetaBit icon={Users} title="People who spoke">{people > 1 ? people : null}</MetaBit>
        </div>
        {company && call.call_label && (
          <div style={{ fontSize: 10.5, ...muted, marginTop: 1,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {call.call_label}
          </div>
        )}
        {refused && (
          <div style={{ marginTop: 3, fontSize: 10, color: "var(--c-600)",
                        display: "inline-flex", alignItems: "center", gap: 3 }}>
            <Ban size={10} /> {refusalLabel(call)}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
          {refused ? "—" : avg ?? "—"}
        </div>
        <div style={{ fontSize: 9.5, ...muted }}>
          {refused ? "not scored" : `${count} scored`}
        </div>
      </div>
    </button>
  );
}

const HISTORY_FILTERS = [
  { key: "all", label: "All" },
  { key: "call", label: "Calls" },
  { key: "meeting", label: "Meetings" },
];

// Every call this person has, newest first, searchable by company or title.
// A list that only said "a call on the 16th" could not answer the question a
// manager actually opens it with — "how did the Acme calls go?".
function CallHistory({ calls, openId, onSelect }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");

  const sorted = useMemo(() => [...calls].sort((a, b) => {
    const ka = a.started_at || a.call_date || "";
    const kb = b.started_at || b.call_date || "";
    return kb.localeCompare(ka) || (b.id - a.id);
  }), [calls]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((c) => (kind === "all" || c.modality === kind)
      && (!q || `${companyOf(c)} ${c.call_label || ""} ${c.project_name || ""}`.toLowerCase().includes(q)));
  }, [sorted, query, kind]);

  const refused = calls.filter(isRefused).length;
  const companies = new Set(calls.map(companyOf).filter(Boolean)).size;
  const talkTime = calls.reduce((sum, c) => sum + (Number(durationOf(c)) || 0), 0);

  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: ".04em", color: "var(--c-600)" }}>
          Call history
        </div>
        <span style={{ fontSize: 10.5, ...muted, textAlign: "right" }}>
          {calls.length} call{calls.length === 1 ? "" : "s"}
          {companies ? ` · ${companies} compan${companies === 1 ? "y" : "ies"}` : ""}
          {talkTime ? ` · ${formatDuration(talkTime)} total` : ""}
        </span>
      </div>
      {refused > 0 && (
        <div style={{ fontSize: 10.5, ...muted, marginTop: 2 }}>
          {refused} not scored — too short, one-sided, or no real exchange. Open one to see why.
        </div>
      )}

      <div style={{ display: "flex", gap: 6, alignItems: "center", margin: "8px 0 6px", flexWrap: "wrap" }}>
        <span style={{ position: "relative", display: "inline-flex", alignItems: "center", flex: "1 1 120px" }}>
          <Search size={11} style={{ position: "absolute", left: 8, color: "var(--c-500)", pointerEvents: "none" }} />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Company or title" aria-label="Search calls"
                 style={{ width: "100%", padding: "5px 8px 5px 24px", fontSize: 11.5, borderRadius: 7,
                          border: "1px solid var(--c-200)", background: "var(--surface)",
                          color: "var(--c-900)" }} />
        </span>
        <div style={{ display: "flex", gap: 3 }}>
          {HISTORY_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setKind(f.key)} style={{
              border: "1px solid var(--c-200)", cursor: "pointer", fontSize: 11, padding: "4px 8px",
              borderRadius: 7, fontWeight: kind === f.key ? 700 : 400,
              background: kind === f.key ? "var(--c-100, rgba(120,120,120,.14))" : "transparent",
              color: kind === f.key ? "var(--c-900)" : "var(--c-500)",
            }}>{f.label}</button>
          ))}
        </div>
      </div>

      <div style={{ maxHeight: 380, overflowY: "auto", margin: "0 -6px" }}>
        {visible.length === 0 && (
          <p style={{ fontSize: 12, ...muted, margin: "8px 6px" }}>No calls match.</p>
        )}
        {visible.map((c) => (
          <CallRow key={c.id} call={c} active={openId === c.id} onClick={() => onSelect(c.id)} />
        ))}
      </div>
    </div>
  );
}

const DETAIL_TABS = [
  { key: "performance", label: "Performance" },
  { key: "transcript", label: "Transcript analysis" },
];

// `focusCall` = {id, at}: open this call, and if `at` is set, play it from
// there. Sent by the patterns panel and the review queue, which name a call and
// a moment and used to leave the reader to go and find both.
export default function Scorecard({ rubric, me, calls, people = [], employeeId, onSelectEmployee,
                                    focusCall = null, onFocusHandled, onOpenAccount, onCallsChanged }) {
  const [selectedCall, setSelectedCall] = useState(null);
  const [detailTab, setDetailTab] = useState("performance");
  const [pendingSeek, setPendingSeek] = useState(null);
  const playerRef = useRef(null);
  const detailRef = useRef(null);
  const seek = (seconds) => playerRef.current?.seek(seconds);
  // The call list is lean -- scores and facts only. The open call's voice
  // track, moments, measures and coaching prose are fetched for that one call
  // and kept, so clicking back to it is instant.
  const [details, setDetails] = useState({});
  const lastShown = useRef(null);
  // Disputes on the open call, so each score can say where its dispute stands.
  const [disputes, setDisputes] = useState([]);
  const [disputeTick, setDisputeTick] = useState(0);
  // {call: {...}, meeting: {...}} — one median per modality, because the two
  // are different crafts scored on disjoint parameter sets and a median across
  // both would average two different questions.
  const [median, setMedian] = useState({});

  // The roster and the selected person are owned by the screen, so choosing
  // somebody here carries to the Growth and Patterns tabs rather than each
  // panel asking again.
  const person = people.find((p) => String(p.employee) === String(employeeId)) || people[0];

  // Switching person must not leave the previous person's call open beside the
  // new person's radar — which is exactly the kind of mismatch that gets a
  // wrong quote read out in a review.
  useEffect(() => {
    setSelectedCall(person?.calls?.[0]?.id ?? null);
  }, [person?.employee]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Declared after the reset above so that, when both fire together (a manager
  // opening someone else's call from the queue), the named call wins.
  useEffect(() => {
    if (!focusCall?.id) return;
    if (person?.calls?.some((c) => c.id === focusCall.id)) {
      setSelectedCall(focusCall.id);
      setDetailTab("performance");
      setPendingSeek({ id: focusCall.id, at: focusCall.at ?? null });
      if (onFocusHandled) onFocusHandled();
    }
  }, [focusCall, person?.employee]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Once the named call is on screen: bring it into view, and play from the
  // moment if one was named. Waits a tick for the player to mount.
  useEffect(() => {
    if (!pendingSeek || pendingSeek.id !== selectedCall) return undefined;
    const timer = setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (pendingSeek.at != null) seek(pendingSeek.at);
      setPendingSeek(null);
    }, 120);
    return () => clearTimeout(timer);
  }, [pendingSeek, selectedCall]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The manager's overlay. Superuser-only server-side, so a rep's fetch is a
  // 403 that is simply swallowed — they get their own radar with no second
  // polygon, which is the intended view rather than a degraded one.
  const track = person?.track;
  const personId = person?.employee;
  useEffect(() => {
    if (!me?.isSuperuser || !track || !personId) { setMedian({}); return; }
    let cancelled = false;
    Promise.all(MODALITIES.map((m) =>
      apiGet(`/RevOps/team-median/?track=${track}&modality=${m.key}&exclude=${personId}`)
        .then((payload) => [m.key, payload])
        .catch(() => [m.key, null]),
    )).then((pairs) => {
      if (!cancelled) setMedian(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [me?.isSuperuser, track, personId]);

  const openId = (person?.calls || []).find((c) => c.id === selectedCall)?.id ?? person?.calls?.[0]?.id ?? null;
  useEffect(() => {
    if (openId == null || details[openId]) return undefined;
    let cancelled = false;
    apiGet(`/RevOps/SellerCallScores/${openId}/`)
      .then((payload) => !cancelled && setDetails((prev) => ({ ...prev, [openId]: payload })))
      .catch(() => !cancelled && setDetails((prev) => ({ ...prev, [openId]: { _failed: true } })));
    return () => { cancelled = true; };
  }, [openId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openId == null) { setDisputes([]); return undefined; }
    let cancelled = false;
    apiGet(`/RevOps/ScoreDisputes/?call=${openId}`)
      .then((payload) => !cancelled && setDisputes(unpackList(payload)))
      .catch(() => !cancelled && setDisputes([]));
    return () => { cancelled = true; };
  }, [openId, disputeTick]);

  if (!calls) return <p style={muted}>Loading…</p>;

  if (!people.length || !person) {
    return (
      <div style={card}>
        <p style={{ ...muted, fontSize: 13, margin: 0 }}>
          No calls scored yet. They appear here automatically once a recording has been scored.
        </p>
      </div>
    );
  }

  if (!person.calls.length) {
    // Selectable-but-empty is a normal state now that the picker comes from the
    // ladder rather than from whoever happens to have calls — and it is one a
    // manager specifically needs to see. An empty radar with no explanation
    // reads as a broken page or, worse, as a rep scoring zero.
    return (
      <div style={card}>
        <strong style={{ fontSize: 15, color: "var(--c-900)" }}>{person.name}</strong>
        <p style={{ ...muted, fontSize: 13, margin: "6px 0 0" }}>
          On the {person.track === "senior" ? "AE" : "SDR"} track, with nothing scored yet.
          That is not a zero — there is simply nothing to read. Calls land here on their own once
          a recording has been scored.
        </p>
      </div>
    );
  }

  const byModality = rubric?.[person.track]?.parameters_by_modality || { call: [], meeting: [] };
  const grouped = { call: [], meeting: [] };
  person.calls.forEach((c) => { (grouped[c.modality] || grouped.meeting).push(c); });
  const dims = dimensionAverages(person.calls);
  const listed = person.calls.find((c) => c.id === selectedCall) || person.calls[0];
  const detail = listed ? details[listed.id] : null;
  const ready = Boolean(detail && !detail._failed);
  // Until the detail arrives, keep showing the previous call dimmed rather
  // than flashing an empty card -- the frame holds, the content catches up.
  // Only ever the SAME person's previous call: another rep's card under this
  // rep's radar is exactly the mismatch that gets a wrong quote read out.
  const fallback = lastShown.current && lastShown.current.employee === listed?.employee ? lastShown.current : null;
  const openCall = ready ? { ...listed, ...detail } : (fallback || listed);
  const pending = !ready;
  if (ready) lastShown.current = openCall;
  const isSelf = people.length === 1 && !me?.isSuperuser;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* ── who, and the two numbers that frame everything below ── */}
      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 20,
                    alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Target size={16} color="var(--blue-600)" />
          {/* Just the name. Who is being viewed is chosen once, in the bar above
              this panel, and carries across every tab — a manager asks "how is
              this person doing" once, not once per screen. Two pickers for one
              selection is how a page ends up disagreeing with itself. */}
          <strong style={{ fontSize: 16, color: "var(--c-900)" }}>
            {isSelf ? "Your scorecard" : person.name}
          </strong>
          <span style={{ fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".05em",
                         padding: "3px 8px", borderRadius: 999,
                         background: "var(--c-100, rgba(120,120,120,.12))" }}>
            {person.track === "senior" ? "AE" : "SDR"}
          </span>
        </div>

        <div style={{ display: "flex", gap: 26, flexWrap: "wrap" }}>
          {[["Voice & delivery", dims.tonality], ["Substance", dims.technicality]].map(([label, v]) =>
            v == null ? null : (
              <div key={label}>
                <div style={{ fontSize: 10, ...muted, textTransform: "uppercase", letterSpacing: ".05em" }}>
                  {label}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--c-900)", lineHeight: 1.15 }}>
                  {v}<span style={{ fontSize: 12, ...muted, fontWeight: 400 }}> / 10</span>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div style={card}>
        <GapBars employeeId={person.employee} track={person.track} />
      </div>

      {/* ── the radars, and the call list beside them ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 14 }}>
        {MODALITIES.map((m) => (
          <RadarBlock key={m.key} modality={m} calls={grouped[m.key] || []}
                      parameters={byModality[m.key] || []} median={median[m.key]}
                      meanings={rubric?.meanings || NO_MEANINGS} />
        ))}

        <CallHistory calls={person.calls} openId={listed?.id} onSelect={setSelectedCall} />
      </div>

      {/* ── the one call the manager is actually looking at ── */}
      {openCall && (
        <div ref={detailRef} style={{ scrollMarginTop: 12 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, borderBottom: "1px solid var(--c-200)" }}>
            {DETAIL_TABS.map(({ key, label }) => (
              <button key={key} onClick={() => setDetailTab(key)} style={{
                padding: "7px 4px", marginBottom: -1, background: "transparent", border: "none",
                cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                color: detailTab === key ? "var(--blue-600)" : "var(--c-500)",
                borderBottom: detailTab === key ? "2px solid var(--blue-600)" : "2px solid transparent",
              }}>{label}</button>
            ))}
            {/* Which call is open, stated where the eye already is. Without
                this, clicking through a list of same-named calls gives no
                confirmation that anything changed. */}
            <span style={{ marginLeft: "auto", fontSize: 11.5, alignSelf: "center",
                           color: "var(--c-800)" }}>
              <strong>{companyOf(listed) || formatDate(listed)}</strong>
              <span style={muted}> · {companyOf(listed) ? formatDate(listed) : listed.call_label}</span>
            </span>
          </div>

          {detailTab === "performance" && detail?._failed && (
            <div className="Login-Error" style={{ marginBottom: 10 }}>Couldn't load this call's details.</div>
          )}
          {detailTab === "performance" && !ready && !fallback && !detail?._failed && (
            <p style={muted}>Loading the call…</p>
          )}
          {detailTab === "performance" && (ready || fallback) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14,
                          opacity: pending ? 0.5 : 1, transition: "opacity .15s" }}>
              <CallCard call={openCall} history={person.calls} onOpenCall={setSelectedCall}
                        playerRef={playerRef} playbookLinks={rubric?.playbook_links || {}}
                        onOpenAccount={onOpenAccount} disputes={disputes}
                        canAdjust={Boolean(me?.isSuperuser)}
                        canDispute={!me?.isSuperuser && isSelf}
                        onAdjusted={(updated) => {
                          if (updated) setDetails((prev) => ({ ...prev, [updated.id]: updated }));
                          if (onCallsChanged) onCallsChanged();
                          setDisputeTick((t) => t + 1);
                        }}
                        onDisputed={() => setDisputeTick((t) => t + 1)} />
              {/* A refused call was not judged: no voice reading and no turning
                  points to show, and two empty panels under "not scored" read
                  as the analysis failing rather than as the call being skipped. */}
              {!isRefused(openCall) && openCall.source !== "crm" && (
                <>
                  <VoiceTimeline call={openCall} onSeek={seek} isSelf={isSelf} />
                  <KeyMoments call={openCall} onSeek={seek} />
                </>
              )}
            </div>
          )}
          {detailTab === "transcript" && (
            <div style={card}>
              <TranscriptAnalyser callId={listed.id} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
